import { type ApolloError, useQuery } from '@apollo/react-hooks';
import gql from 'graphql-tag';
import type { PublicCellData } from '../GSTodoList';
import {
  modeOfStudyOptions,
  programmeTypeOptions,
} from '../../../../Application/components';
import { getOptionsLabel } from '../../../../Monitor/components';
import { MONITOR_LIST_TEXT } from '../../../lang';
import { OFFER_STATUS } from '../../../GSAssessment';

export type FailedColumnData = PublicCellData & {
  entryYear: string;
};

type GSFailedList = {
  failedList: FailedColumnData[];
  totalPage: number;
  count: number;
};

type GetGSFailedList = {
  failedListData: GSFailedList;
  loading: boolean;
  error: ApolloError | undefined;
  refetch: () => void;
};

const GET_FAILED_LIST = gql`
  query (
    $filter: FailedApplicationListFilter!
    $paginator: PaginatorOptions!
    $sort: ApplicationSort
  ) {
    failedApplicationPaginated(
      filter: $filter
      paginator: $paginator
      sort: $sort
    ) {
      data {
        _id
        applicationNo
        applicant {
          name {
            surname
            givenName
          }
        }
        programmeChoice {
          department
          programme
          programmeType
          proposedChiefSupervisor
          proposedChiefSupervisorBackup
          rejectedByFirstChoice
          researchArea
          studyMode
          entryYear
        }
        released
        revoked
      }
      paginator {
        total_pages
        count
      }
    }
  }
`;
const NO_INDICATION = 'Not Indicated';
export const useGetFailedList = (
  filter: any,
  sort: any,
  paginator: any,
): GetGSFailedList => {
  const { data, loading, error, refetch } = useQuery(GET_FAILED_LIST, {
    variables: { filter, sort, paginator },
    fetchPolicy: 'network-only',
  });

  const lapList = data?.failedApplicationPaginated?.data ?? [];
  const failedList: FailedColumnData[] = lapList.map((element: any) => {
    const {
      _id: applicationId,
      applicationNo,
      applicant: {
        name: { surname, givenName },
      },
      programmeChoice: {
        department,
        programme,
        programmeType,
        researchArea,
        studyMode,
        entryYear,
        proposedChiefSupervisor,
        proposedChiefSupervisorBackup,
        rejectedByFirstChoice,
      },
      released,
      revoked,
    } = element;

    // TODO will add display name  in inWhichSupervisorList
    const inWhichSupervisorList: string = !(proposedChiefSupervisor as boolean)
      ? NO_INDICATION
      : !(rejectedByFirstChoice as boolean)
        ? proposedChiefSupervisor
        : !(proposedChiefSupervisorBackup as boolean)
          ? NO_INDICATION
          : proposedChiefSupervisorBackup;

    const studyModeLabel = getOptionsLabel(modeOfStudyOptions, studyMode);
    const programmeTypeLabel = getOptionsLabel(
      programmeTypeOptions,
      programmeType,
    );
    const PROGRAMME_LEVEL = MONITOR_LIST_TEXT.PROGRAMME_LEVEL;

    const getStatus = (
      isReleased?: boolean,
      isRevoked?: boolean,
    ): OFFER_STATUS => {
      if (isRevoked) {
        return OFFER_STATUS.REVOKED;
      }
      if (isReleased) {
        return OFFER_STATUS.RELEASED;
      }
      if (isReleased === false) {
        return OFFER_STATUS.RELEASING;
      }
      return OFFER_STATUS.READY_TO_RELEASE;
    };

    return {
      applicationId,
      applicationNo,
      applicantName: `${surname?.toUpperCase()} ${givenName}`,
      department,
      progLevel: PROGRAMME_LEVEL[programme as keyof typeof PROGRAMME_LEVEL],
      progType: programmeTypeLabel,
      researchField: researchArea,
      studyMode: studyModeLabel,
      proposedChiefSupervisor: inWhichSupervisorList,
      entryYear,
      status: getStatus(released, revoked),
    };
  });

  const totalPage =
    data?.failedApplicationPaginated?.paginator?.total_pages ?? 1;
  const count = data?.failedApplicationPaginated?.paginator?.count ?? 0;

  return {
    failedListData: {
      failedList,
      totalPage,
      count,
    },
    loading,
    error,
    refetch,
  };
};
